import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Event, IRedirectDomain, Tags } from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import {
  CardV2 as Card,
  DatePicker,
  Fieldset,
  ImagePicker,
  Input,
  UniversalSelect,
} from '@feathr/components';
import TagsField from '@feathr/extender/components/TagsField';
import { flattenError } from '@feathr/hooks';

import type { IProjectSettingsFields } from './useProjectSettingsFields';

interface IProjectSettingsCardsProps {
  advertisersUrl: string;
  event: Event;
  fields: IProjectSettingsFields;
  hasInvites: boolean;
  /** Defaults to the store's. */
  tags?: Tags;
}

function getOptionLabel(option: IRedirectDomain): string {
  return option.domain;
}

/** The project details, schedule, and domains cards, shared by project settings and Perch. */
function ProjectSettingsCards({
  advertisersUrl,
  event,
  fields,
  hasInvites,
  tags,
}: Readonly<IProjectSettingsCardsProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    advOptions,
    endDate,
    errors,
    handleChangeMktgPageRdr,
    handleChangeRefPageRdr,
    handleChangeTags,
    handleEndDateInput,
    handleSelectAdvertiser,
    isAgency,
    mktPageRdr,
    redirectDomains,
    refPageRdr,
    startDate,
  } = fields;

  // The event is loaded at the page level, so the selects only need to track their own fetch.
  const domainsLoading = redirectDomains.isPending;
  const domainPlaceholder = domainsLoading ? t('Loading...') : t('Select a domain...');
  const domainOptions = redirectDomains.models.map((model) => model.toJS());
  const selectedAdvertiser = advOptions.find(({ id }) => id === event.get('subadvertiser'));

  return (
    <>
      <Card name={'project-details-card'}>
        <Card.Header
          description={t("Your project's name, logo, and tags.")}
          title={t('Project details')}
        />
        <Card.Content addVerticalGap={true}>
          <ImagePicker attribute={'logo'} model={event} />
          <Input
            attribute={'name'}
            label={t('Project name')}
            model={event}
            type={'text'}
            validationError={flattenError(errors.name)}
          />
          <TagsField
            collection={tags}
            context={'event'}
            helpText={t(
              'Search for an existing tag, or type a new name to create one. Tags group related projects so you can filter for them in the projects page.',
            )}
            label={t('Tags')}
            onChange={handleChangeTags}
            value={event.get('tag_ids', [])}
          />
          <Input
            attribute={['email_preferences_alias']}
            helpText={t(
              'Feathr automatically creates an email subscription for each project. Customize the label recipients see when managing their subscription preferences. Defaults to the project name.',
            )}
            isClearable={true}
            label={t('Custom email subscription label')}
            model={event}
            placeholder={event.name}
            type={'text'}
          />
          {isAgency && (
            <UniversalSelect<IUniversalSelectOption>
              helpText={
                <>
                  <p>
                    If this project will primarily house campaigns that advertise on behalf of
                    another organization, please choose that organization here. New campaigns will
                    use this advertiser by default, but it can be changed on a per-campaign basis.
                  </p>
                  <p>
                    Add organizations to this list on the{' '}
                    <Link to={advertisersUrl}>Advertisers page</Link> in your account settings.
                  </p>
                </>
              }
              label={t('Default advertiser')}
              name={'default-advertiser'}
              onSelectSingle={handleSelectAdvertiser}
              options={advOptions}
              value={selectedAdvertiser}
            />
          )}
        </Card.Content>
      </Card>

      <Card name={'schedule-card'}>
        <Card.Header
          description={t('Set when this project starts and ends.')}
          title={t('Schedule')}
        />
        <Card.Content>
          {/*
           * Clearing a date saves correctly, but the model stays dirty (Save stays enabled) until
           * reload: the PATCH response omits the now-null field, and rachis only cleans attributes
           * echoed back (collection.ts -> setAttributeClean).
           */}
          {/* onChangeRaw lets a typed out-of-range end date reach the model so date_end can flag
            it. The range error shows only on the End field. */}
          <Fieldset>
            <DatePicker
              attribute={'date_start'}
              endDate={endDate}
              isClearable={true}
              label={t('Start date')}
              maxDate={endDate}
              model={event}
              selectsStart={true}
              startDate={startDate}
            />
            <DatePicker
              attribute={'date_end'}
              endDate={endDate}
              isClearable={true}
              label={t('End date')}
              minDate={startDate}
              model={event}
              onChangeRaw={handleEndDateInput}
              selectsEnd={true}
              startDate={startDate}
              validationError={flattenError(errors.date_end)}
            />
          </Fieldset>
        </Card.Content>
      </Card>

      <Card name={'domains-card'}>
        <Card.Header
          description={t("Choose the domains used for this project's pages.")}
          title={t('Domains')}
        />
        <Card.Content>
          <Fieldset direction={'column'}>
            {hasInvites && (
              <UniversalSelect<IRedirectDomain>
                disabled={domainsLoading}
                getOptionLabel={getOptionLabel}
                isLoading={domainsLoading}
                label={t('Invite page domain')}
                name={'invite-page-domain'}
                onSelectSingle={handleChangeRefPageRdr}
                options={domainOptions}
                placeholder={domainPlaceholder}
                value={refPageRdr?.toJS()}
              />
            )}
            <UniversalSelect<IRedirectDomain>
              disabled={domainsLoading}
              getOptionLabel={getOptionLabel}
              isLoading={domainsLoading}
              label={t('Landing page domain')}
              name={'landing-page-domain'}
              onSelectSingle={handleChangeMktgPageRdr}
              options={domainOptions}
              placeholder={domainPlaceholder}
              value={mktPageRdr?.toJS()}
            />
          </Fieldset>
        </Card.Content>
      </Card>
    </>
  );
}

export default observer(ProjectSettingsCards);
